import {
  PhoneOff, PhoneCall, PhoneMissed, CheckCircle2, Ghost, Zap, Moon, Ban,
  XCircle, Star, RefreshCw, AlertTriangle, HelpCircle, Building2, Euro
} from "lucide-react";

// Spot status meta: label + badge classes + icon
export const SPOT_STATUS = {
  jamais_appele: { label: "Jamais appelé", cls: "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.7)] border-[rgba(24,24,24,0.12)]", icon: PhoneOff },
  en_prospection: { label: "En prospection", cls: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8] border-[rgba(47,111,237,0.25)]", icon: PhoneCall },
  nrp: { label: "NRP", cls: "bg-[rgba(238,114,46,0.14)] text-[#8A3A10] border-[rgba(238,114,46,0.3)]", icon: PhoneMissed },
  oui_oral: { label: "Oui à l'oral", cls: "bg-[rgba(0,180,216,0.14)] text-[#036672] border-[rgba(0,180,216,0.3)]", icon: HelpCircle },
  oui_ghoste: { label: "Oui ghosté", cls: "bg-[rgba(139,92,246,0.14)] text-[#6D28D9] border-[rgba(139,92,246,0.3)]", icon: Ghost },
  actif: { label: "Actif", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] border-[rgba(31,157,85,0.28)]", icon: Zap },
  dormant: { label: "Dormant", cls: "bg-[rgba(24,24,24,0.06)] text-[rgba(24,24,24,0.55)] border-[rgba(24,24,24,0.12)]", icon: Moon },
  refus_temporaire: { label: "Refus temporaire", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226] border-[rgba(214,69,69,0.28)]", icon: XCircle },
  spot_nul: { label: "Spot nul", cls: "bg-[rgba(24,24,24,0.08)] text-[rgba(24,24,24,0.6)] border-[rgba(24,24,24,0.15)]", icon: AlertTriangle },
  blackliste: { label: "Blacklisté / fermé", cls: "bg-[rgba(120,0,0,0.12)] text-[#7A1F1F] border-[rgba(120,0,0,0.3)]", icon: Ban },
  a_annualiser: { label: "À annualiser", cls: "bg-[rgba(252,219,83,0.35)] text-[#5A4200] border-[rgba(252,219,83,0.6)]", icon: RefreshCw },
  annualise: { label: "Annualisé", cls: "bg-[rgba(252,219,83,0.55)] text-[#4A3600] border-[rgba(240,190,20,0.7)]", icon: Star },
  accord_obtenu: { label: "Accord obtenu", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] border-[rgba(31,157,85,0.28)]", icon: CheckCircle2 },
};

export const BOOKING_STATUS = {
  ferme: { label: "Ferme", dot: "#1F9D55", cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F] border-[rgba(31,157,85,0.3)]" },
  au_crayon: { label: "Au crayon", dot: "#EE722E", cls: "bg-[rgba(238,114,46,0.14)] text-[#8A3A10] border-[rgba(238,114,46,0.3)]" },
  proposition_envoyee: { label: "Proposition envoyée", dot: "#2F6FED", cls: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8] border-[rgba(47,111,237,0.25)]" },
  a_confirmer: { label: "À confirmer", dot: "#E9B800", cls: "bg-[rgba(252,219,83,0.4)] text-[#5A4200] border-[rgba(252,219,83,0.65)]" },
  annule: { label: "Annulé", dot: "#D64545", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226] border-[rgba(214,69,69,0.28)]" },
  // --- Statuts issus de l'import historique (fichier de suivi) ---
  realise: { label: "Réalisé", dot: "#64748B", cls: "bg-[rgba(100,116,139,0.14)] text-[#334155] border-[rgba(100,116,139,0.3)]" },
  planifie: { label: "Planifié", dot: "#6366F1", cls: "bg-[rgba(99,102,241,0.12)] text-[#4338CA] border-[rgba(99,102,241,0.3)]" },
};

export const NIVEAU_PARTENARIAT = {
  ponctuel: "Ponctuel",
  a_annualiser: "À annualiser",
  annualise: "Annualisé",
};

export const TIER = {
  A: { label: "A", cls: "bg-[rgba(31,157,85,0.15)] text-[#0F5A2F] border-[rgba(31,157,85,0.35)]", desc: "Fort flux (>1000/j)" },
  B: { label: "B", cls: "bg-[rgba(252,219,83,0.4)] text-[#5A4200] border-[rgba(252,219,83,0.7)]", desc: "Flux moyen (500-1000/j)" },
  C: { label: "C", cls: "bg-[rgba(214,69,69,0.12)] text-[#9B2226] border-[rgba(214,69,69,0.3)]", desc: "Faible flux (<500/j)" },
};

// Contrast helper: readable text over an arbitrary hex color
export function textColorFor(hex) {
  if (!hex) return "#181818";
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L < 0.5 ? "#FFFFFF" : "#181818";
}

// Tinted planning cell background from CDP color
export function cdpCellStyle(color) {
  const c = color || "#F89F63";
  return {
    background: `color-mix(in srgb, #ffffff 80%, ${c} 20%)`,
    borderColor: `color-mix(in srgb, #ffffff 45%, ${c} 55%)`,
  };
}

// Rebooking TERRAIN : code couleur UNIQUE (violet) distinct des teintes CDP (pâles)
// et des statuts. Traitement volontairement plus soutenu (fond + bordure pleine)
// pour être identifiable d'un seul coup d'œil, quelle que soit la couleur du CDP.
export const FIELD_REBOOK_COLOR = "#7C3AED"; // violet-600
export function fieldRebookCellStyle() {
  return {
    background: `color-mix(in srgb, #ffffff 82%, ${FIELD_REBOOK_COLOR} 18%)`,
    borderColor: FIELD_REBOOK_COLOR,
  };
}

// Créneau ANNULÉ (conservé, barré) : fond grisé neutre + fines hachures diagonales
// pour un rendu « rayé » type Excel, tout en gardant la carte lisible.
export function cancelledCellStyle() {
  return {
    background:
      "repeating-linear-gradient(-45deg, rgba(214,69,69,0.06) 0, rgba(214,69,69,0.06) 6px, rgba(148,148,148,0.10) 6px, rgba(148,148,148,0.10) 12px)",
    borderColor: "rgba(214,69,69,0.35)",
  };
}

export function frenchDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

export function frenchDateShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

// Puce de jour compacte et PARLANTE (jour de semaine + date), pour choisir vite.
//   "2026-08-31" -> "Lun. 31 août"   ·   "2026-09-03" -> "Mer. 3 sept."
export function frenchDayChip(iso) {
  if (!iso) return "";
  try {
    const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso);
    if (isNaN(d.getTime())) return String(iso);
    const s = d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch { return iso; }
}

// Date + heure (ex. « 24/07/2026 à 14:32 ») — pour les historiques d'actions.
export function frenchDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export const DEPT_ICON = Building2;
export const PAYANT_ICON = Euro;

// Date longue en français, avec l'ordinal du 1er du mois.
//   "2026-08-01" -> "samedi 1er août 2026"
//   "2026-08-02" -> "dimanche 2 août 2026"
export function frenchDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    if (isNaN(d.getTime())) return iso;
    const weekday = d.toLocaleDateString("fr-FR", { weekday: "long" });
    const month = d.toLocaleDateString("fr-FR", { month: "long" });
    const day = d.getDate();
    const dayStr = day === 1 ? "1er" : String(day);
    return `${weekday} ${dayStr} ${month} ${d.getFullYear()}`;
  } catch { return iso; }
}

// Date longue « pleine », capitalisée et jour sur 2 chiffres.
//   "2026-08-08" -> "Vendredi 08 Août 2026"
export function frenchDateFull(iso) {
  if (!iso) return "";
  try {
    const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso);
    if (isNaN(d.getTime())) return iso;
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    const weekday = cap(d.toLocaleDateString("fr-FR", { weekday: "long" }));
    const month = cap(d.toLocaleDateString("fr-FR", { month: "long" }));
    const day = String(d.getDate()).padStart(2, "0");
    return `${weekday} ${day} ${month} ${d.getFullYear()}`;
  } catch { return iso; }
}


// Formate une liste de dates (Date ou ISO) en texte FR naturel :
//   1 date  -> "lundi 4 mai 2026"
//   2 dates -> "lundi 4 et mardi 5 mai 2026" (mois/année condensés si identiques)
//   3+      -> "lundi 4, mardi 5 et mercredi 6 mai 2026"
export function formatDatesFr(dates) {
  const arr = (dates || [])
    .map((d) => (d instanceof Date ? d : new Date(d)))
    .filter((d) => d && !isNaN(d.getTime()))
    .sort((a, b) => a - b);
  if (arr.length === 0) return "";
  const last = arr[arr.length - 1];
  const sameMY = arr.every((d) => d.getMonth() === last.getMonth() && d.getFullYear() === last.getFullYear());
  const parts = arr.map((d, i) => {
    const isLast = i === arr.length - 1;
    const opts = sameMY && !isLast
      ? { weekday: "long", day: "numeric" }
      : { weekday: "long", day: "numeric", month: "long", year: "numeric" };
    return d.toLocaleDateString("fr-FR", opts);
  });
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} et ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}`;
}

// Affiche un numéro FR avec le 0 initial (filet de sécurité côté affichage,
// ne modifie jamais la donnée en base — le backend normalise déjà à l'import).
export function frenchPhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^0-9+]/g, "");
  // Numéro national à 9 chiffres sans 0 initial (ex: 612345678 -> 0612345678)
  if (/^[1-9][0-9]{8}$/.test(digits)) return "0" + digits;
  return String(raw).trim();
}

// --- Contacts : helpers de lecture multi-téléphones / multi-emails ---
export function contactName(c) {
  if (!c) return "";
  return [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim();
}
export function contactPhones(c) {
  if (!c) return [];
  return [c.telephone, c.telephone_2, c.telephone_3]
    .map((t) => frenchPhone(t))
    .filter(Boolean);
}
export function contactEmails(c) {
  if (!c) return [];
  return [c.email, c.email_2].map((e) => (e || "").trim()).filter(Boolean);
}

// --- Statuts horodatés : phrase "depuis le / le ..." ---
// "le" = évènement ponctuel ; "depuis le" = état continu ; null = pas de date.
const STATUS_SINCE_MODE = {
  accord_obtenu: "le", nrp: "le", blackliste: "le", refus_temporaire: "le", spot_nul: "le",
  en_prospection: "depuis le", actif: "depuis le", dormant: "depuis le",
  oui_oral: "depuis le", oui_ghoste: "depuis le", a_annualiser: "depuis le", annualise: "depuis le",
  jamais_appele: null,
};

export function statusSinceLabel(spot) {
  if (!spot) return "";
  const meta = SPOT_STATUS[spot.status];
  const label = meta?.label || spot.status;
  const mode = STATUS_SINCE_MODE[spot.status];
  const since = spot.status_since;
  let txt = label;
  if (mode && since) txt = `${label} ${mode} ${frenchDate(since)}`;
  if (spot.status === "nrp" && spot.nrp_attempts) {
    txt += ` (${spot.nrp_attempts} tentative${spot.nrp_attempts > 1 ? "s" : ""})`;
  }
  return txt;
}

// Partie temporelle seule (sans le libellé) : "depuis le JJ/MM/AAAA" / "le JJ/MM/AAAA".
export function statusSinceShort(spot) {
  if (!spot) return "";
  const mode = STATUS_SINCE_MODE[spot.status];
  const since = spot.status_since;
  let txt = "";
  if (mode && since) txt = `${mode} ${frenchDate(since)}`;
  if (spot.status === "nrp" && spot.nrp_attempts) {
    txt += `${txt ? " " : ""}(${spot.nrp_attempts} tentative${spot.nrp_attempts > 1 ? "s" : ""})`;
  }
  return txt;
}
